import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'

import { invokePayment } from '../../store/payment.action';
import { selectCreditCardTokenResponse, selectInvoiceresponse, selectPaymentresponse, selectPaymentSession, selectPaymentStateEvents, selectPaymentToken } from '../../store/payment.selector';
import { CreditCardTokenResponse, InvoiceResponse, PaymentResponseModel, PaymentSession, PaymentStateEvents, ProcessTextToPayRequest } from '../../store/payment.state';
import { CreditCardPaymentComponent } from '../credit-card-payment/credit-card-payment.component';
import { PaymentAPIService } from '../../api/payment-api.service';
import { PaymentEffects } from '../../store/payment.effects';
import { CardSkeletalComponent } from '../card-skeletal/card-skeletal.component';


@Component({
  selector: 'app-payment',
  imports: [CommonModule, CreditCardPaymentComponent, ReactiveFormsModule, CardSkeletalComponent],
  templateUrl: './payment.component.html',
  providers: [PaymentAPIService, PaymentEffects]
})
export class PaymentComponent {
  paymentForm!: FormGroup;
  private subscriptions$ = new Subscription();
  paymentToken: string = '';
  invoiceResponse!: InvoiceResponse;
  paymentResponseModel!: PaymentResponseModel;
  isLoading: boolean = true;
  paymentSession!: PaymentSession;
  isExpired: boolean = false;

  selectInvoiceresponse$;
  selectPaymentStateEvents$;
  selectPaymentSession$;
  selectCreditCardTokenResponse$;
  selectPaymentresponse$;
  selectPaymentToken$;
  isPayNowClicked: boolean = false;
  creditCardToken = '';
  tokenErrorMessage = '';
  transactionStatus = '';

  @ViewChild(CreditCardPaymentComponent) creditCardPaymentComponent!: CreditCardPaymentComponent;

  constructor(private store: Store,
    private formBuilder: FormBuilder
  ) {
    this.paymentForm = this.formBuilder.group({
      patientName: [''],
      transactionAmount: ['', [Validators.required, Validators.pattern(/^[0-9]+(\.[0-9]{2})?$/)]],
    })
    this.selectInvoiceresponse$ = this.store.select(selectInvoiceresponse);
    this.selectPaymentStateEvents$ = this.store.select(selectPaymentStateEvents);
    this.selectPaymentSession$ = this.store.select(selectPaymentSession);
    this.selectCreditCardTokenResponse$ = this.store.select(selectCreditCardTokenResponse);
    this.selectPaymentresponse$ = this.store.select(selectPaymentresponse);
    this.selectPaymentToken$ = this.store.select(selectPaymentToken);
  }

  ngOnInit() {
    this.subscriptions$.add(this.selectInvoiceresponse$.subscribe((data: InvoiceResponse) => this.bindSelectInvoiceResponse(data)));
    this.subscriptions$.add(this.selectPaymentStateEvents$.subscribe((data: PaymentStateEvents) => this.bindSelectPaymentStateEvents(data)));
    this.subscriptions$.add(this.selectPaymentSession$.subscribe((data: PaymentSession) => this.bindSelectPaymentSession(data)));
    this.subscriptions$.add(this.selectCreditCardTokenResponse$.subscribe((data: CreditCardTokenResponse) => this.bindSelectCreditCardToken(data)));
    this.subscriptions$.add(this.selectPaymentresponse$.subscribe((data: PaymentResponseModel) => this.bindSelectPaymentResponseModel(data)));
    this.subscriptions$.add(this.selectPaymentToken$.subscribe((data: string) =>  this.paymentToken = data));
  }

  private bindSelectInvoiceResponse(data: InvoiceResponse): void {
    if (data) {
      this.invoiceResponse = data;
      if (this.invoiceResponse && this.invoiceResponse.dueAmount) {
        this.paymentForm.patchValue({ patientName: this.invoiceResponse.patientName, transactionAmount: this.invoiceResponse.dueAmount });
      }
      else {
        this.isExpired = true;
      }
    }
  }

  bindSelectCreditCardToken(creditCardTokenResponse: CreditCardTokenResponse) {
    if (creditCardTokenResponse.status || creditCardTokenResponse.error) {
      if (creditCardTokenResponse.status === "SUCCESS") {
        this.creditCardToken = creditCardTokenResponse.ccTempToken;
        if (this.isPayNowClicked && this.creditCardToken) {
          this.processPayment();
        }
      }
      else {
        if (this.isPayNowClicked) {
          this.tokenErrorMessage = creditCardTokenResponse.error ? creditCardTokenResponse.error.message : creditCardTokenResponse.reason;
        }
        this.isPayNowClicked = false;
        this.creditCardPaymentComponent.showCardAlert();
      }
    }
    else {
      this.creditCardToken = "";
    }
  }

  processPayment() {
    if (this.paymentForm.valid && this.paymentForm.value.transactionAmount > 0) {
      if(this.paymentForm.value.transactionAmount > 999) {
        return;
      }
      if (this.creditCardToken && this.creditCardPaymentComponent.getCardHolderName()) {
        const paymentRequest: ProcessTextToPayRequest = {
          encodedString: this.paymentToken,
          transactionAmount: this.paymentForm.value.transactionAmount.toString(),
          ccTempToken: this.creditCardToken,
          sessionToken: this.paymentSession.sessionToken || '',
          notes: 'TextToPay',
        };
        this.store.dispatch(invokePayment({ paymentRequestModel: paymentRequest }));
      }
      else if (!this.creditCardPaymentComponent.getCardHolderName()) {
        this.creditCardPaymentComponent.showCardAlert();
      }
      else {
        this.isPayNowClicked = true;
        const isCallToken = this.creditCardPaymentComponent.getToken();
        if (!isCallToken) {
          this.isPayNowClicked = false;
        }
      }
    }
  }

  private bindSelectPaymentStateEvents(data: PaymentStateEvents): void {
    this.isLoading = data.loading;
  }

  private bindSelectPaymentSession(data: PaymentSession): void {
    this.paymentSession = data;
  }

  private bindSelectPaymentResponseModel(data: PaymentResponseModel): void {
    if (data.patients.length > 0) {
      this.paymentResponseModel = data;
      this.transactionStatus = data.transactionStatus;
      if (!this.transactionStatus) {
        this.transactionStatus = 'Failed';
      }
    }
  }

  getInitials(name: string): string {
    if (!name) return '';
    return name
      .split(/[\s,]+/)
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  }

  ngOndestroy() {
    this.subscriptions$.unsubscribe();
  }
}
